/* AUTO-SYNCED from PsyKit.io/components/bilateral/LivePreview.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import BallCanvas from "./BallCanvas";

interface LivePreviewProps {
  // BallCanvas pass-through
  hz: number;
  ballSize: number;
  ballColor: string;
  canvasBg: string;
  isRunning: boolean;
  isStopping: boolean;
  onPositionChange: (xNorm: number, hz: number, elapsedSeconds: number) => void;
  onPassComplete: (side: "left" | "right") => void;
  onStopComplete: () => void;
  // Start / Stop
  onStart: () => void;
  onStop: () => void;
  // Countdown timer (state owned by page.tsx)
  timerOn: boolean;
  setTimerOn: (v: boolean) => void;
  timerDuration: number;
  setTimerDuration: (v: number) => void;
  timerDisplay: string;
  timerExpiring: boolean;
  // Session clock (therapist-only). null until first Start.
  firstStartAt: number | null;
}

const STORAGE_KEY = "psykit.preview.pos";
const WIDGET_W = 296;
const WIDGET_H = 296;
const MARGIN = 24;

function loadPosition(): { left: number; top: number } | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { left?: number; top?: number };
    if (typeof parsed.left !== "number" || typeof parsed.top !== "number") return null;
    return { left: parsed.left, top: parsed.top };
  } catch {
    return null;
  }
}

function clampToViewport(left: number, top: number): { left: number; top: number } {
  const maxLeft = Math.max(0, window.innerWidth - WIDGET_W);
  const maxTop = Math.max(0, window.innerHeight - WIDGET_H);
  return {
    left: Math.min(Math.max(0, left), maxLeft),
    top: Math.min(Math.max(0, top), maxTop),
  };
}

function defaultPosition(): { left: number; top: number } {
  // Bottom-right with margin.
  return clampToViewport(
    window.innerWidth - WIDGET_W - MARGIN,
    window.innerHeight - WIDGET_H - MARGIN,
  );
}

function formatClock(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function LivePreview(props: LivePreviewProps) {
  const {
    hz, ballSize, ballColor, canvasBg, isRunning, isStopping,
    onPositionChange, onPassComplete, onStopComplete,
    onStart, onStop,
    timerOn, setTimerOn, timerDuration, setTimerDuration, timerDisplay, timerExpiring,
    firstStartAt,
  } = props;

  // ── Position (fixed) ────────────────────────────────────────────────
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);

  // Hydrate on mount (client-only so SSR doesn't mismatch).
  useEffect(() => {
    const stored = loadPosition();
    const initial = stored ? clampToViewport(stored.left, stored.top) : defaultPosition();
    setPos(initial);
  }, []);

  // Re-clamp on viewport resize so the widget never strands off-screen.
  useEffect(() => {
    const onResize = () => {
      setPos((p) => (p ? clampToViewport(p.left, p.top) : p));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!pos) return;
    // Only left-button drags.
    if (e.button !== 0 && e.pointerType === "mouse") return;
    dragOffsetRef.current = { dx: e.clientX - pos.left, dy: e.clientY - pos.top };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  }, [pos]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const off = dragOffsetRef.current;
    if (!off) return;
    const next = clampToViewport(e.clientX - off.dx, e.clientY - off.dy);
    setPos(next);
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragOffsetRef.current) return;
    dragOffsetRef.current = null;
    try { (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId); } catch {}
    setPos((p) => {
      if (p) {
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
        } catch {}
      }
      return p;
    });
  }, []);

  // ── Session clock ───────────────────────────────────────────────────
  // Force a re-render every second while the clock is running. State value
  // itself doesn't matter — we read firstStartAt + Date.now() at render time.
  const [, force] = useState(0);
  useEffect(() => {
    if (firstStartAt == null) return;
    const id = setInterval(() => force((n) => (n + 1) & 0xffff), 1000);
    return () => clearInterval(id);
  }, [firstStartAt]);

  const clockText = firstStartAt == null ? "0:00" : formatClock(Date.now() - firstStartAt);

  // ── Render ──────────────────────────────────────────────────────────
  // Don't paint anything until we know our position — avoids a flash at (0,0).
  if (!pos) return null;

  const dragging = dragOffsetRef.current != null;

  return (
    <div
      role="region"
      aria-label="Live preview"
      className="fixed z-40 select-none"
      style={{
        left: pos.left,
        top: pos.top,
        width: WIDGET_W,
        height: WIDGET_H,
      }}
    >
      <div
        className="relative h-full w-full overflow-hidden rounded-2xl border bg-[var(--color-bg-deep)]"
        style={{
          borderColor: "var(--color-border)",
          boxShadow:
            "0 30px 80px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        {/* Drag handle / header (caplabel + pulse) — modeled on
            components/landing/BouncingBallPreview.tsx */}
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className="relative flex items-center justify-between px-4 pt-3 pb-2"
          style={{
            cursor: dragging ? "grabbing" : "grab",
            touchAction: "none",
          }}
        >
          <span className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-[var(--color-gold-soft)]">
            Live preview
          </span>
          <span
            aria-hidden="true"
            className="preview-pulse h-2 w-2 rounded-full bg-[var(--color-gold)]"
            style={{ boxShadow: "0 0 0 4px rgba(229,185,112,0.2)" }}
          />
        </div>

        {/* Canvas stage */}
        <div
          className="relative mx-3 mb-2 h-[140px] overflow-hidden rounded-xl"
          style={{ background: "var(--color-void, #050810)" }}
        >
          <BallCanvas
            hz={hz}
            ballSize={ballSize}
            ballColor={ballColor}
            canvasBg={canvasBg}
            isRunning={isRunning}
            isStopping={isStopping}
            onPositionChange={onPositionChange}
            onPassComplete={onPassComplete}
            onStopComplete={onStopComplete}
            fillContainer
          />
        </div>

        {/* Controls strip */}
        <div className="flex items-center gap-2 px-3 pb-1">
          {isRunning ? (
            <button
              onClick={onStop}
              className="flex-1 rounded-[4px] border px-3 py-1.5 font-[var(--font-barlow)] text-[10px] font-medium uppercase tracking-[0.12em] transition-colors"
              style={{
                borderColor: "#8B2E2E",
                color: "#C84444",
                background: "transparent",
              }}
            >
              ■ Stop
            </button>
          ) : (
            <button
              onClick={onStart}
              disabled={isStopping}
              className="flex-1 rounded-[4px] border-2 px-3 py-1.5 font-sans text-[11px] font-semibold uppercase tracking-wide transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-40"
              style={{
                borderColor: "var(--color-gold)",
                background: "var(--color-gold)",
                color: "var(--color-bg-deep)",
              }}
            >
              ▶ {isStopping ? "Stopping…" : "Start"}
            </button>
          )}

          <div
            className="flex items-center gap-1 rounded-[4px] border px-2 py-1"
            style={{
              borderColor: "var(--color-border)",
              background: "var(--color-navy-mid)",
            }}
            title="Session clock — counts from first Start, ignores pauses"
          >
            <span
              className="font-[var(--font-barlow)] text-[8px] uppercase tracking-[0.14em]"
              style={{ color: "var(--color-text-muted)" }}
            >
              Clk
            </span>
            <span
              className="font-[var(--font-dm-mono)] text-[12px]"
              style={{ color: "var(--color-gold-soft)" }}
            >
              {clockText}
            </span>
          </div>
        </div>

        {/* Timer strip */}
        <div
          className="mx-3 mb-3 mt-1 flex items-center justify-between gap-2 rounded-[4px] border px-2 py-1.5"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-navy-mid)",
          }}
        >
          <label className="flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={timerOn}
              onChange={(e) => setTimerOn(e.target.checked)}
              className="sr-only"
            />
            <span
              className="relative h-4 w-7 rounded-full transition-colors"
              style={{ background: timerOn ? "var(--color-gold)" : "var(--color-border)" }}
            >
              <span
                className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform ${
                  timerOn ? "translate-x-3.5" : "translate-x-0.5"
                }`}
              />
            </span>
            <span
              className="font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.12em]"
              style={{ color: "var(--color-text-muted)" }}
            >
              Timer
            </span>
          </label>

          {timerOn ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setTimerDuration(Math.max(30, timerDuration - 30))}
                className="flex h-5 w-5 items-center justify-center rounded-[3px] border font-mono text-[12px] leading-none"
                style={{
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-muted)",
                  background: "transparent",
                }}
                aria-label="Decrease timer by 30s"
              >
                −
              </button>
              <span
                className="font-[var(--font-dm-mono)] min-w-[34px] text-center text-[11px]"
                style={{ color: "var(--color-text-primary)" }}
              >
                {Math.floor(timerDuration / 60)}:{String(timerDuration % 60).padStart(2, "0")}
              </span>
              <button
                onClick={() => setTimerDuration(Math.min(1800, timerDuration + 30))}
                className="flex h-5 w-5 items-center justify-center rounded-[3px] border font-mono text-[12px] leading-none"
                style={{
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-muted)",
                  background: "transparent",
                }}
                aria-label="Increase timer by 30s"
              >
                +
              </button>
              <span
                className="ml-2 font-[var(--font-dm-mono)] text-[12px]"
                style={{ color: timerExpiring ? "#C84444" : "var(--color-gold-soft)" }}
              >
                {timerDisplay}
              </span>
            </div>
          ) : (
            <span
              className="font-[var(--font-dm-mono)] text-[10px]"
              style={{ color: "var(--color-text-muted)" }}
            >
              off
            </span>
          )}
        </div>
      </div>

      <style>{`
        @keyframes preview-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        .preview-pulse { animation: preview-pulse 2s infinite; }
        @media (prefers-reduced-motion: reduce) {
          .preview-pulse { animation: none; }
        }
      `}</style>
    </div>
  );
}
